package one.devsbox.www.ui.company.edit

import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.setValue
import compose.assets.Svg
import compose.assets.svg.ContentSaveOutline
import kotlinx.coroutines.launch
import one.devsbox.shared.ads.SharedIO
import one.devsbox.shared.devices.ApplicationType
import one.devsbox.www.io.Repository
import one.devsbox.www.ui.widgets.ScreenContentAction
import one.devsbox.www.tools.ArgsId
import one.devsbox.www.www_portal.generated.resources.*
import tk.mallumo.compose.navigation.viewmodel.NavigationViewModel
import tk.mallumo.compose.navigation.viewmodel.VM
import tk.mallumo.utils.DataState

@VM
class CompanyEVM : NavigationViewModel() {
    private lateinit var ctx: CompanyECtx

    var contentActions by mutableStateOf<List<ScreenContentAction>>(emptyList())
        private set

    var warningMessage by mutableStateOf<String?>(null)
        private set

    var state by mutableStateOf<DataState<SharedIO.CompanySimple>>(DataState.Loading())
        private set

    var name by mutableStateOf("")
    var appTypeMask by mutableStateOf(0)

    fun setup(ctx: CompanyECtx) {
        this.ctx = ctx
        if (state is DataState.Loading) {
            loadCompany(ctx.args.id)
        }
    }

    private fun loadCompany(id: Long) {
        state = DataState.Loading()
        scope.launch {
            runCatching {
                if (id > 0) {
                    Repository.ads.company.get(id)
                } else {
                    SharedIO.CompanySimple(
                        id = -1,
                        name = "",
                        appTypeMask = ApplicationType.WWW_CAMPAIGN.mask
                    )
                }
            }.onSuccess { company ->
                name = company.name
                appTypeMask = company.appTypeMask
                state = DataState.Result(company)
                setupActions()
            }.onFailure {
                state = DataState.Error(it.message ?: "Unknown error")
                contentActions = emptyList()
            }
        }
    }

    private fun setupActions() {
        contentActions = buildList {
            add(
                ScreenContentAction(
                    text = Res.string.ui_company_dialog_btn_confirm,
                    color = ctx.colorPrimaryContainer,
                    icon = Svg.ContentSaveOutline
                ) {
                    action.save()
                }
            )
        }
    }

    interface Action {
        fun save()
        fun clearWarning()
        fun toggleAppType(appType: ApplicationType, enabled: Boolean)
        fun back()
        fun reload()
    }

    val action = object : Action {
        override fun back() {
            ctx.nav.up()
        }

        override fun reload() {
            loadCompany(ctx.args.id)
        }

        override fun toggleAppType(appType: ApplicationType, enabled: Boolean) {
            if (enabled) {
                appTypeMask += appType.mask
            } else {
                appTypeMask -= appType.mask
            }
        }

        override fun save() {
            val currentState = state.entry ?: return
            val trimmedName = name.trim()
            if (trimmedName.isEmpty()) {
                warningMessage = "Company name cannot be empty."
                return
            }
            state = DataState.Loading(currentState)
            scope.launch {
                runCatching {
                    val body = SharedIO.CompanyEdit(
                        name = trimmedName,
                        appTypeMask = appTypeMask
                    )
                    if (currentState.id > 0) {
                        Repository.ads.company.change(ctx.args.id, body)
                    } else {
                        Repository.ads.company.add(body)
                    }
                }.onSuccess {
                    ctx.nav.up()
                }.onFailure {
                    warningMessage = it.message ?: "Unknown error"
                    state = DataState.Result(currentState)
                }
            }
        }

        override fun clearWarning() {
            warningMessage = null
        }
    }

    override fun onRelease() {}
}
